import { useEffect, useRef, useState } from 'react';
import { TabItem } from './Tabs';

/**
 * A hook to manage the state of the tabs.
 */
export function useTabs({
  defaultActiveIndex = 0,
  items,
  tablistId,
}: {
  /**
   * The index of the tab that should be active by default.
   */
  defaultActiveIndex?: number;
  /**
   * The items to be rendered as tabs and the panel.
   */
  items: TabItem[];
  /**
   * The id of the tablist.
   */
  tablistId: string;
}) {
  /**
   * The index of the active tab.
   */
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);

  /**
   * The index of the tab that is focused.
   * This is used to manage the keyboard navigation for a11y.
   */
  const focusIndexRef = useRef(-1);

  /**
   * The reference of div element to the tab panels wrapper.
   * This is used to manage the height of the tab panels wrapper to animate the transition.
   */
  const tabPanelsWrapperRef = useRef<HTMLDivElement>(null);

  const tabs = items.map((item, index) => ({
    ...item,
    index,
    isActive: index === activeIndex,
    tabId: `tab-${tablistId}-${index}`,
    tabPanelId: `tabpanel-${tablistId}-${index}`,
    onBlur: () => setFocusIndex(-1),
    onClick: () => setActiveIndex(index),
    onFocus: () => setFocusIndex(index),
  }));

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    update(true);

    /**
     * Observe the height of the active tab panel to animate the transition.
     */
    const hasResizeObserver = 'ResizeObserver' in window;
    if (!hasResizeObserver) return;
    const resizeObserver = new ResizeObserver(() => update(false));

    tabs.forEach(({ tabPanelId, isActive }) => {
      if (!isActive) return;
      const tabPanel = document.getElementById(tabPanelId);
      if (tabPanel) {
        resizeObserver.observe(tabPanel);
      }
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [activeIndex]);

  /**
   * Set height of tabpanels wrapper to the height and slide by the active index.
   * @param slide Controls whether to slide the tab panels.
   */
  function update(slide?: boolean) {
    const wrapper = tabPanelsWrapperRef.current;
    if (!wrapper) return;

    tabs.forEach(({ tabPanelId, isActive }) => {
      const tabPanel = document.getElementById(tabPanelId);
      if (!tabPanel) return;

      if (slide) {
        tabPanel.removeAttribute('hidden');
      }

      if (isActive) {
        const { height } = tabPanel.getBoundingClientRect();
        wrapper.style.height = `${height}px`;
      }
    });

    wrapper.style.transform = `translateX(-${activeIndex * 100}%)`;
  }

  return { focusIndexRef, tabs, activeIndex, tabPanelsWrapperRef, onTransitionEnd, setActiveIndex };

  function onTransitionEnd() {
    const inActiveTab = tabs.find(({ isActive }) => !isActive);
    if (!inActiveTab) return;
    document.getElementById(inActiveTab.tabPanelId)?.setAttribute('hidden', 'true');
  }

  function setFocusIndex(index: number) {
    focusIndexRef.current = index;
  }

  function handleKeyDown(event: KeyboardEvent) {
    const focusIndex = focusIndexRef.current;
    if (focusIndex === -1) return;

    let nextIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (focusIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (focusIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        break;
    }

    const nextTab = document.getElementById(tabs[nextIndex]?.tabId);
    if (!nextTab) return;

    event.preventDefault();
    nextTab.focus();
  }
}
